/** The values necessary to render an Arc Reactor chart. */
import { Either } from "../shared/functional"; // Use of "@app/..." forbidden due to 1.0 usage of Arc Reactor.
import { AttestationRateDays } from "../projects/enumerators";

/** Defines a colorized arc on the Reactor and the value it displays. */
export interface ReactorBand {
  /** The hex for the color to render. Format: #000000. */
  color: string;

  /** The value of the band. Must be between 0-100. */
  value: number;

  /** The meaning of the band. This is used for the aria description. */
  name: string;
}

export interface AdequacyReactorConfiguration {
  green: number,
  yellow: number,
  red: number,
  blue: number,
  projected: number,
  projectedIndex: number,
  index: number | string,

  isPercent?: boolean,
  monochrome?: boolean; // sets all colors to grayscale
  route?: string;
}

/**
 * The configuration necessary to render the accuracy arc of the Arc Reactor.
 */
export interface AccuracyReactorConfiguration {
  bands: ReactorBand[];

  index: number | string;
  indeterminantText?: string;
  isIndeterminant?: boolean;
  isPercent?: boolean;
  route?: string;
}

export interface AttestationReactorConfiguration {
  attestationRateDays?: AttestationRateDays,
  green: number, // DAH: The colors are temporarily being set to grayscale. [editor's note: this comment is originally from October 2019]
  yellow: number,
  red: number,
  index: number | string,

  isPercent?: boolean,
  route?: string;
}

export interface ReactorConfiguration {
  composite: number | string;
  compositeOverride?: "adequacy" | "accuracy" | "attestation";
  compositeRoute?: string;

  adequacy: AdequacyReactorConfiguration;
  accuracy: AccuracyReactorConfiguration | null;
  attestation: AttestationReactorConfiguration;

  onRouteChange?: (route: string) => void;
}

/**
 * Validates the state of a reactor configuration.
 * Returns the set of errors, if any; else, returns an empty error.
 *
 * @param configuration The configuration to validate.
 */
export function validateConfiguration(configuration: ReactorConfiguration): Either<string, void> {
  return composeValidations(
    configuration,
    validateDimensions,
    validateBands,
    validateComposite,
  );
}

/**
 * Combines validations, producing either a clean validation or a composed error message.
 * @param configuration The configuration to check.
 * @param validations The validations to combine.
 */
function composeValidations(configuration: ReactorConfiguration, ...validations: Array<(c: ReactorConfiguration) => Either<string, void>>): Either<string, void> {
  if (!configuration) {
    return Either.left("Configuration can not be null or undefined");
  }

  const validationResults = validations.map((v) => v(configuration));
  const errors = validationResults.filter((vr) => vr && vr.isLeft());

  switch (errors.length) {
    case 0:
      return Either.right(null);
    case 1:
      return errors[0];
    default:
      return errors.reduce(
        (accError, nextError) =>
          Either.left(accError.fromLeft("") + `\n${nextError.fromLeft("")}`),
        Either.left("Multiple Errors:"),
      );
  }
}

/**
 * Validates that the configuration contains at least one dimension (Accuracy, Adequacy, or Attestation).
 * Validates that inactive dimensions are explicitly set to null.
 * @param configuration The configuration in question.
 */
function validateDimensions(configuration: ReactorConfiguration): Either<string, void> {
  const dimensions: Array<{ dimension: string, value: AccuracyReactorConfiguration | AdequacyReactorConfiguration | AttestationReactorConfiguration }> = [
    { dimension: "accuracy", value: configuration.accuracy },
    { dimension: "adequacy", value: configuration.adequacy },
    { dimension: "attestation", value: configuration.attestation },
  ];

  if (!dimensions.some(({ value }) => !!value)) {
    return Either.left("Missing either Accuracy, Adequacy, or Attestation");
  }

  const undefinedDimensions = dimensions
    .filter(({ value }) => value === undefined)
    .map(({ dimension }) => `Inactive dimension "${dimension}" must be explicitly set to null`)
    .join("\n");
  if (undefinedDimensions) {
    return Either.left(undefinedDimensions);
  }

  return Either.right(null);
}

/**
 * Validates that the bands of the chart are able to render.
 * @param configuration The configuration to check.
 */
function validateBands(configuration: ReactorConfiguration): Either<string, void> {
  // If there are no banded fields, there is nothing to check.
  if (!configuration.accuracy) {
    return Either.right(null);
  }

  const bands = configuration.accuracy.bands;
  if (!bands || bands.length === 0) {
    return Either.left("At least one band must be present");
  }

  for (const band of bands) {
    const matchArray = band.color && band.color.match(/^#([0-9a-f]{6})$/i);
    const match = matchArray && matchArray[1];
    if (!match) {
      return Either.left(`Invalid Color Format: "${band.color}"`);
    }
  }

  return Either.right(null);
}

/**
 * Validates that the composite setup is valid for a given configuration.
 *
 * If an override is specified then the corresponding field must be available.
 * @param configuration The configuration to check.
 */
function validateComposite(configuration: ReactorConfiguration): Either<string, void> {
  if (configuration.compositeOverride) {
    const found = !!configuration[configuration.compositeOverride];
    return found
      ? Either.right(null)
      : Either.left(`Cannot override composite for non-existing field "${configuration.compositeOverride}"`);
  }

  return Either.right(null);
}
